import { Button, Callout, Collapsible, Skeleton } from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { trackEvent } from "@/lib/tracking";
import { isActionAvailable } from "@/utils/available-actions";
import { TestTubeDiagonal } from "lucide-react";
import { useCallback, useState } from "react";
import { useCarePlanSummary } from "../network/useCarePlanSummary";
import { CareplanAvailableAction } from "../network/useCarePlanSummary";
import { LabOrderV2 } from "./LabOrderV2";
import { MissingPatientDetails } from "./OrderEntry/MissingPatientDetails";
import { useCreateOrder } from "./network/useCreateOrder";
import { useLabOrderEntryActions } from "./network/useLabOrderEntryActions";
import { useLabOrdersByEncounter } from "./network/useLabOrdersByEncounter";

interface LabsProps {
  patientId: string;
  encounterId: string;
  carePlanId: string;
  totalLabs: number;
}

export const Labs = ({
  patientId,
  encounterId,
  carePlanId,
  totalLabs,
}: LabsProps) => {
  const [open, setOpen] = useState(false);

  const {
    data: labOrdersByEncounterDataV2,
    isLoading: isLoadingLabOrdersByEncounterV2,
    isError: isErrorLabOrdersByEncounterV2,
  } = useLabOrdersByEncounter({
    patientId,
    encounterId,
  });

  const { data: careplanSummary } = useCarePlanSummary(encounterId);
  const careplanAvailableActions = careplanSummary?.availableActions;

  const hasReadLabsPrivs = isActionAvailable(
    CareplanAvailableAction.READ_LAB,
    careplanAvailableActions,
  );
  const hasAddLabsPrivs = isActionAvailable(
    CareplanAvailableAction.ADD_LAB,
    careplanAvailableActions,
  );

  const { data: labOrderEntryActions } = useLabOrderEntryActions({
    patientId: patientId,
  });

  const isPatientOrderingAvailable =
    labOrderEntryActions?.patientOrderingAvailability?.orderingAvailable;

  const createOrder = useCreateOrder({
    patientId,
    encounterId,
  });

  const handleCreateOrder = useCallback(() => {
    trackEvent(TRACKERS.CREATE_LAB_ORDER);
    createOrder.mutate({});
  }, [createOrder]);

  const testsCount = totalLabs;

  if (!hasReadLabsPrivs) {
    return null;
  }

  return (
    <Collapsible
      title="Labs"
      titleIcon={<TestTubeDiagonal className="w-4 h-4" />}
      headerSize="sm"
      status={
        testsCount
          ? `${testsCount.toString()} Test${testsCount > 1 ? "s" : ""}`
          : "None"
      }
      onOpenChange={(open) => {
        trackEvent(open ? TRACKERS.EXPAND_LABS : TRACKERS.COLLAPSE_LABS);
        setOpen(open);
      }}
      open={open}
      controlled={true}
    >
      <div className="grid gap-2">
        {isErrorLabOrdersByEncounterV2 && (
          <Callout
            title="Something went wrong"
            description="Unable to load labs"
            variant="danger"
          />
        )}
        {isLoadingLabOrdersByEncounterV2 &&
          Array.from({ length: 3 }).map((_, index) => (
            //  biome-ignore lint/suspicious/noArrayIndexKey: unneeded for loading skeleton
            <Skeleton className="h-8" key={index} />
          ))}
        {!isPatientOrderingAvailable && (
          <MissingPatientDetails
            patientId={patientId}
            missingPatientFields={
              labOrderEntryActions?.patientOrderingAvailability
                ?.missingFields ?? []
            }
          />
        )}
        {labOrdersByEncounterDataV2?.orders.map((order, index) => (
          <LabOrderV2
            key={`${order.requestGroup.id}`}
            index={index}
            requestGroup={order.requestGroup}
            serviceRequests={order.tests}
            statusSummary={order.statusSummary}
            actionSummary={order.actionSummary}
            encounterId={encounterId}
            carePlanId={carePlanId}
            patientId={patientId}
            readOnly={!hasAddLabsPrivs}
          />
        ))}
        {!isLoadingLabOrdersByEncounterV2 &&
          !isErrorLabOrdersByEncounterV2 &&
          isPatientOrderingAvailable && (
            <Button
              size="xs"
              variant="secondary"
              className="w-fit"
              onClick={handleCreateOrder}
              isLoading={createOrder.isPending}
              disabled={!hasAddLabsPrivs}
            >
              Add Lab Order
            </Button>
          )}
      </div>
    </Collapsible>
  );
};
